/**
 * Eine Tour im Ganzen.
 *
 * ═══ WARUM DIESE SEITE NEU AUFGETEILT IST ═══
 *
 * Sie bestand vorher aus sechs Karten, und drei davon beantworteten dieselbe
 * Frage aus verschiedenen Richtungen: „Eckdaten" zeigte den Kunden, das
 * Ladedatum und die Orte — „Bearbeiten" zeigte dieselben Felder noch einmal als
 * Formular, „Wer fährt" und „Preis" jeweils eine Handvoll Angaben in einem
 * eigenen Kasten. Wer den Wagen wechseln wollte, musste an drei Stellen
 * schauen, um sicher zu sein, dass er nicht die falsche erwischt.
 *
 * Jetzt gilt: **eine Sache, eine Stelle.** Die Eckdaten sind EIN Kasten. Er
 * zeigt alles, was die Tour ausmacht — Kunde, Strecke, Menge, wer fährt, was
 * sie kostet — und derselbe Kasten wird zum Formular, wenn man „Bearbeiten"
 * drückt. Kein zweiter Ort, an dem dasselbe Feld noch einmal steht.
 *
 * Einen eigenen Block behalten nur die drei Dinge, die keine Eckdaten sind,
 * sondern eigene Sammlungen: die Dokumente, die Notiz und der Verlauf.
 *
 * ═══ WARUM DER STATUS GANZ OBEN STEHT UND EIN AUSWAHLFELD IST ═══
 *
 * Der Zustand einer Tour hat zwei Hälften. Die eine ergibt sich („die Fahrt
 * läuft noch", „ein Beleg fehlt") — die rechnet die Datenbank aus, und niemand
 * kann sie stellen. Die andere ist eine Entscheidung des Büros: freigeben oder
 * stornieren. Genau diese Hälfte gehört in ein Auswahlfeld, denn sie ist das
 * Einzige an dieser Seite, was ein Mensch am Status ändern kann. Was die Straße
 * sagt, steht daneben — als Auskunft, nicht als Knopf.
 *
 * ═══ DER VERLAUF ═══
 *
 * „Wer hat den Preis geändert" und „seit wann steht da Martin und nicht Sabine"
 * sind Fragen, die im Streitfall kommen — und ohne Aufzeichnung steht Aussage
 * gegen Aussage. Geschrieben wird der Verlauf von der Datenbank selbst; aus dem
 * Backoffice kann ihn niemand ergänzen oder wegräumen.
 *
 * Ändern lässt sich alles nur, solange keine Rechnungsnummer steht. Das ist die
 * eine Sperre des Systems, und sie hängt am Feld, nicht am Status.
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Seitenkopf } from '../Rahmen';
import {
  Fahrernotizblock,
  Feld,
  Fehlerkasten,
  Freigabeinhalt,
  Karte,
  Knopf,
  Konflikthinweis,
  Ladehinweis,
  Marker,
  Meldung,
  Rueckfrage,
  Bestaetigungsdialog,
  Textfeld,
  Zahlfeld,
} from '../gestaltung/bausteine';
import { useAbfrage } from '../daten/abfrage';
import { Speicherfehler } from '../daten/speichern';
import { alsKlarfehler, type Klarfehler } from '../daten/fehler';
import { belegAdresse, ARTTEXT as BELEGART } from '../daten/belege';
import {
  abrechnungFreigabeZuruecknehmen,
  abrechnungFreigeben,
  auftragAendern,
  auftragLaden,
  auftragEntfernen,
  auftragStornieren,
  auftragStornoZuruecknehmen,
  belegeFreigeben,
  belegfreigabeZuruecknehmen,
  beleglageText,
  betrag,
  dokumentHochladen,
  dokumentVerwerfen,
  euro,
  fahrtBesetzen,
  fahrtNachtragen,
  freigabeFrage as freigabeFrageErmitteln,
  mengenmarke,
  notizSetzen,
  PREISARTTEXT,
  streckeNachtragen,
  ZUSTANDFARBE,
  ZUSTANDTEXT,
  type Auftragszeile,
  type Dokumentzeile,
  type Fahrtzeile,
  type Freigabefrage,
  type Streckenzeile,
  type Verlaufszeile,
  type Zustand,
} from '../daten/auftraege';
import {
  fahrzeugeLaden,
  kundenLaden,
  orteLaden,
  vorlagenLaden,
} from '../daten/stammdaten';
import { mitarbeiterLaden } from '../daten/mitarbeiter';
import { fahrerZurWahl, type Fahrerwahl } from '../daten/fahrerwahl';
import { EINHEITTEXT } from './StreckenSeite';
import { zahlAlsText, zahlLesen } from '../daten/zahl';
import { anschriftszeile } from '../daten/anschrift';
import { Auswahlsuche } from '../gestaltung/Auswahlsuche';
import {
  abgleichen,
  behalten as meinsBehalten,
  eigeneAenderungen,
  eingeben,
  uebernehmen as fremdUebernehmen,
  type Felder,
  type Formularstand,
} from '../daten/ausgangsstand';

function datum(wert: string | null): string {
  if (!wert) return '—';
  return new Date(wert).toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function zeitpunkt(wert: string): string {
  return new Date(wert).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* ------------------------------------------------------------------ Status */

/**
 * In welcher Lage die Tour aus Sicht des Büros ist — und damit, welche
 * Handlungen überhaupt angeboten werden.
 *
 * Das ist KEIN Status, den jemand stellt. Es gab hier einmal ein Auswahlfeld
 * dafür, neben dem zusätzlich der errechnete Zustand stand; aus dem Büro kam
 * dazu „das versteht keiner". Zwei Statusbegriffe nebeneinander sind einer zu
 * viel. Geblieben ist der errechnete Zustand zum Lesen — und dieser Wert hier
 * entscheidet nur noch, welcher Knopf danebensteht.
 */
type Buerostatus = 'offen' | 'freigegeben' | 'storniert' | 'abgerechnet';

function buerostatus(auftrag: Auftragszeile): Buerostatus {
  if (auftrag.rechnungsnummer !== null) return 'abgerechnet';
  if (auftrag.zustand === 'storniert') return 'storniert';
  if (auftrag.abrechnung_freigegeben) return 'freigegeben';
  return 'offen';
}

/* -------------------------------------------------------------- Die Seite */

export function AuftragSeite() {
  const { id = '' } = useParams();
  const gehZu = useNavigate();
  const detail = useAbfrage(() => auftragLaden(id), [id]);
  const fahrzeuge = useAbfrage(fahrzeugeLaden, []);
  const leute = useAbfrage(mitarbeiterLaden, []);
  const kunden = useAbfrage(kundenLaden, []);
  const orte = useAbfrage(orteLaden, []);
  const vorlagen = useAbfrage(vorlagenLaden, []);

  const [fehler, setFehler] = useState<Klarfehler | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [bearbeiten, setBearbeiten] = useState(false);
  const [stornoOffen, setStornoOffen] = useState(false);
  const [stornogrund, setStornogrund] = useState('');
  const [loeschenOffen, setLoeschenOffen] = useState(false);
  /** Nicht leer, solange die Rückfrage vor einer Freigabe offen ist. */
  const [freigabeFrage, setFreigabeFrage] = useState<Freigabefrage | null>(null);
  const [bild, setBild] = useState<{ beleg: Dokumentzeile; adresse: string } | null>(
    null,
  );

  /**
   * ═══ AUFFRISCHEN AUCH NACH EINEM FEHLER ═══
   *
   * Speichern besteht aus mehreren Schritten: erst der Auftrag, dann jede
   * Fahrt. Scheitert der zweite Schritt, steht der erste bereits in der
   * Datenbank — und mit ihm ein neuer `geaendert_am`. Wer dann nicht
   * auffrischt, hält weiter den alten Stand: Jeder weitere Klick endet mit
   * „inzwischen von jemand anderem geändert", obwohl es niemand war, und
   * heraus kommt man nur durch Neuladen der Seite.
   *
   * Dasselbe gilt für einen echten Konflikt: Auch dort ist der frische Stand
   * das Erste, was man braucht.
   *
   * ═══ WAS DAS AUFFRISCHEN MIT OFFENEN FORMULAREN MACHT (H1.3b, E-039) ═══
   *
   * Früher hing das Eckdatenformular am Stand (`key = geaendert_am`) und baute
   * sich nach jedem Auffrischen neu auf — damit war auch halb Getipptes weg,
   * sobald irgendwo auf der Seite eine Notiz oder ein Beleg gespeichert wurde.
   * Bloß an der ID aufgehängt hätte es nach einem Konflikt dagegen alte Werte
   * gegen den frischen Stand verglichen und eine fremde Änderung an einem
   * Feld, das man nicht angefasst hat, beim nächsten Speichern still
   * zurückgedreht. Jetzt hängen Eckdatenformular und Notizkarte an der Tour
   * und merken sich ihren Ausgangsstand (`daten/ausgangsstand.ts`): Der
   * frische Stand fließt in die nicht angefassten Felder, die eigenen
   * Eingaben bleiben, und wo beide dasselbe Feld geändert haben, entscheidet
   * der Nutzer am Feld.
   */
  async function mitFehlern(tun: () => Promise<void>) {
    setFehler(null);
    setLaeuft(true);
    try {
      await tun();
    } catch (problem) {
      setFehler(
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem),
      );
    } finally {
      // Gesperrt bleibt, bis der neue Stand DA ist. Vorher war „Notiz
      // speichern" nach dem Speichern kurz wieder frei — ein zweiter Klick
      // schickte den alten `geaendert_am` und bekam „Jemand war schneller".
      await detail.auffrischen();
      setLaeuft(false);
    }
  }

  if (detail.laedt && !detail.daten) return <Ladehinweis />;
  // Nur wenn es noch GAR keine Daten gibt, ersetzt der Fehler die Seite.
  // Scheitert ein Auffrischen, bleibt der alte Stand mit offenen Formularen
  // stehen, und der Fehler steht darüber — sonst wären die Eingaben weg, nur
  // weil das Netz kurz weg war (H1.3b).
  if (detail.fehler && !detail.daten) {
    return (
      <>
        <Seitenkopf titel="Tour" />
        <Fehlerkasten fehler={detail.fehler} nochmal={detail.auffrischen} />
        <div style={{ marginTop: 14 }}>
          <Knopf art="zweit" onClick={() => gehZu('/disposition')}>
            Zurück zur Übersicht
          </Knopf>
        </div>
      </>
    );
  }
  if (!detail.daten) return null;

  const { kopf, fahrten, strecken, dokumente, verlauf } = detail.daten;
  const jetzt = buerostatus(kopf);

  /**
   * ═══ DIE SPERRE IST DIESELBE WIE IN DER DATENBANK ═══
   *
   * `auftrag_noch_aenderbar()` lässt Änderungen zu, solange keine
   * Rechnungsnummer steht UND der Auftrag nicht storniert ist. Die Oberfläche
   * kannte nur die erste Hälfte — an einer stornierten Tour standen deshalb
   * „Bearbeiten", das Preisfeld und die Belegfreigabe weiter offen. Wer sie
   * benutzte, bekam keinen ehrlichen Korb, sondern die Meldung „inzwischen von
   * jemand anderem geändert": Die Zeilenregel lässt die Zeile schlicht
   * verschwinden, und `aendern()` kann von außen nicht unterscheiden, ob sie
   * gesperrt oder fremdgeändert ist. Eine falsche Erklärung ist schlimmer als
   * ein gesperrter Knopf.
   */
  const gesperrt = kopf.rechnungsnummer !== null || kopf.zustand === 'storniert';
  /** Nur die Rechnungsnummer schließt auch den Status. Ein Storno ist rücknehmbar. */
  const abgerechnet = kopf.rechnungsnummer !== null;

  const fahrer = fahrerZurWahl(leute.daten ?? []);
  const wagen = (fahrzeuge.daten ?? []).filter((f) => f.aktiv);

  /**
   * ═══ WANN EINE TOUR GELÖSCHT WERDEN DARF ═══
   *
   * Nur solange sie nichts hinterlassen hat. Sobald ein Beleg fotografiert oder
   * eine Fuhre gezählt wurde, ist die Tour gefahren worden — Löschen hieße dann,
   * einen Nachweis zu vernichten, und der richtige Weg heißt Stornieren.
   *
   * Rechnungsnummer und Storno deckt `gesperrt` bereits ab; der Knopf sitzt
   * ohnehin innerhalb dieser Prüfung.
   *
   * Dieselbe Bedingung steht in `auftrag_entfernen`. Hier versteckt sie nur den
   * Knopf, damit niemand ihn drückt und einen Korb bekommt — verbindlich ist
   * sie in der Datenbank.
   */
  const loeschbar = dokumente.length === 0 && Number(kopf.fuhren ?? 0) === 0;

  function freigeben() {
    void mitFehlern(async () => {
      // Aus dem Storno heraus freigeben heißt: erst zurücknehmen, dann
      // freigeben. Die Datenbank würde die Freigabe an einer stornierten Tour
      // sonst folgenlos schlucken.
      if (buerostatus(kopf) === 'storniert') {
        await auftragStornoZuruecknehmen(kopf.id);
      }
      await abrechnungFreigeben(kopf.id);
    });
  }


  return (
    <>
      <Seitenkopf
        titel={kopf.nummer ?? 'Tour ohne Nummer'}
        erklaerung={`${kopf.kunde ?? '—'} · ${kopf.ladeort ?? '—'} → ${kopf.entladeort ?? '—'}`}
        werkzeuge={
          <Knopf art="zweit" onClick={() => gehZu('/disposition')}>
            Zurück
          </Knopf>
        }
      />

      {fehler && (
        <div style={{ marginBottom: 16 }}>
          <Fehlerkasten fehler={fehler} />
        </div>
      )}

      {detail.fehler && (
        <div style={{ marginBottom: 16 }}>
          <Fehlerkasten fehler={detail.fehler} nochmal={detail.auffrischen} />
        </div>
      )}

      <div className="stapel" style={{ gap: 16 }}>
        <Karte
          titel="Eckdaten"
          werkzeuge={
            !gesperrt && (
              <div className="reihe" style={{ gap: 8 }}>
                <Knopf
                  art={bearbeiten ? 'still' : 'zweit'}
                  klein
                  onClick={() => setBearbeiten((o) => !o)}
                >
                  {bearbeiten ? 'Bearbeiten beenden' : 'Bearbeiten'}
                </Knopf>
                {/* Der Knopf erscheint nur, wo Löschen überhaupt in Frage
                    kommt. An einer gefahrenen Tour steht er gar nicht erst da
                    — ein Knopf, der zuverlässig einen Korb gibt, ist schlimmer
                    als keiner. */}
                {loeschbar && (
                  <Knopf art="gefahr" klein onClick={() => setLoeschenOffen(true)}>
                    Löschen
                  </Knopf>
                )}
              </div>
            )
          }
        >
          <div className="stapel" style={{ gap: 14 }}>
            {/* ═══ EIN ZUSTAND, DANEBEN DIE HANDLUNGEN ═══

                Vorher stand hier ein Auswahlfeld „Status" und daneben „Aus den
                Daten" — zwei Statusbegriffe nebeneinander. Aus dem Büro kam
                dazu: „Das versteht keiner." Zu Recht: Ein Zustand, den man
                teils stellen und teils nur ablesen kann, ist kein Zustand,
                sondern zwei, und welcher gilt, war nicht zu sehen.

                Jetzt gibt es EINEN Zustand. Er wird gelesen, nicht gestellt —
                das rechnet die Datenbank aus. Was das Büro entscheiden kann,
                sind Handlungen, und Handlungen sind Knöpfe. Links steht, was
                IST; rechts, was man TUN kann.

                Dazu die drei Angaben, die die Frage „worum geht es hier"
                beantworten: für wen, wohin, wann. Ohne Beschriftungen — wer
                eine Tour öffnet, soll nicht erst Feldnamen lesen müssen. */}
            <div className="tourkopf">
              <div className="tourkopf__lage">
                <div className="tourkopf__marken">
                  <Marker
                    gross
                    art={ZUSTANDFARBE[kopf.zustand as Zustand] ?? 'neutral'}
                  >
                    {ZUSTANDTEXT[kopf.zustand] ?? kopf.zustand}
                  </Marker>
                  {kopf.rechnungsnummer && (
                    <Marker art="neutral">Rechnung {kopf.rechnungsnummer}</Marker>
                  )}
                </div>
                <span className="tourkopf__kunde">{kopf.kunde ?? 'Ohne Kunde'}</span>
                {(kopf.ladeort || kopf.entladeort) && (
                  <div className="tourkopf__strecke">
                    {kopf.ladeort ?? '—'} → {kopf.entladeort ?? '—'}
                  </div>
                )}
                <div className="tourkopf__termin">
                  {kopf.ladedatum
                    ? `Geplant für ${datum(kopf.ladedatum)}`
                    : 'Ohne Ladedatum'}
                </div>
                {/* Wer freigegeben hat, gehört dazu — aber als leise Zeile.
                    Ein grüner Kasten direkt unter der grünen Marke sagte
                    zweimal dasselbe. */}
                {kopf.abrechnung_freigegeben &&
                  !abgerechnet &&
                  kopf.abrechnung_freigegeben_von_name && (
                    <div className="tourkopf__termin">
                      Freigegeben von {kopf.abrechnung_freigegeben_von_name}
                    </div>
                  )}
              </div>

              {/* Angeboten wird nur, was in diesem Zustand auch geht. Fünf
                  gleichrangige Knöpfe machten aus einer Entscheidung eine
                  Suche; ein Knopf, der nichts bewirkt, kostet Vertrauen. */}
              {!abgerechnet && (
                <div className="tourkopf__handlungen">
                  {jetzt === 'storniert' ? (
                    <Knopf
                      art="zweit"
                      disabled={laeuft}
                      onClick={() =>
                        void mitFehlern(() => auftragStornoZuruecknehmen(kopf.id))
                      }
                    >
                      Storno zurücknehmen
                    </Knopf>
                  ) : (
                    <>
                      {jetzt === 'freigegeben' ? (
                        <Knopf
                          art="still"
                          disabled={laeuft}
                          onClick={() =>
                            void mitFehlern(() =>
                              abrechnungFreigabeZuruecknehmen(kopf.id),
                            )
                          }
                        >
                          Freigabe zurücknehmen
                        </Knopf>
                      ) : (
                        <Knopf
                          art="haupt"
                          disabled={laeuft}
                          onClick={() => {
                            setStornoOffen(false);
                            const frage = freigabeFrageErmitteln(kopf);
                            if (frage) {
                              setFreigabeFrage(frage);
                              return;
                            }
                            freigeben();
                          }}
                        >
                          Zur Abrechnung freigeben
                        </Knopf>
                      )}
                      <Knopf
                        art="gefahr"
                        disabled={laeuft}
                        onClick={() => {
                          setFreigabeFrage(null);
                          setStornoOffen(true);
                        }}
                      >
                        Tour stornieren
                      </Knopf>
                    </>
                  )}
                </div>
              )}
            </div>

            {kopf.zustand === 'storniert' && (
              <Meldung art="warnung" titel="Storniert">
                {kopf.storno_grund}
                {kopf.storno_grund && ' — '}
                Solange der Storno steht, lässt sich an der Tour nichts ändern.
                Über „Storno zurücknehmen“ oben wird sie wieder geöffnet.
              </Meldung>
            )}

            {/* ══ DIE RÜCKFRAGE, DIE NICHT IMMER KOMMT ══

                Sie kommt nur, wenn es etwas zu sagen gibt — etwas fehlt, oder
                der Fahrer hat etwas notiert — und sie sagt, WAS.
                Eine Rückfrage, die bei jeder Freigabe erscheint, wird nach der
                dritten blind weggeklickt und schützt dann vor nichts mehr. */}
            {freigabeFrage && (
              <Rueckfrage
                titel="Trotzdem zur Abrechnung freigeben?"
                bestaetigungstext="Trotzdem freigeben"
                laeuft={laeuft}
                abbrechen={() => setFreigabeFrage(null)}
                bestaetigen={() => {
                  setFreigabeFrage(null);
                  freigeben();
                }}
              >
                <Freigabeinhalt frage={freigabeFrage} />
                Freigeben geht trotzdem — die Beleglage bleibt daneben stehen,
                wie sie ist. Zurücknehmen kannst du die Freigabe jederzeit.
              </Rueckfrage>
            )}

            {/* ══ LÖSCHEN IST ENDGÜLTIG, ALSO WIRD GEFRAGT ══

                Die Rückfrage nennt die Tour beim Namen und erklärt den
                Unterschied zum Stornieren. „Wirklich löschen?" allein
                beantwortet die Frage nicht, die man in dem Moment hat —
                nämlich, ob man gerade das Falsche tut. */}
            {loeschenOffen && (
              <Rueckfrage
                titel="Diese Tour endgültig löschen?"
                bestaetigungstext="Endgültig löschen"
                art="gefahr"
                laeuft={laeuft}
                abbrechen={() => setLoeschenOffen(false)}
                bestaetigen={() => {
                  setLoeschenOffen(false);
                  void mitFehlern(async () => {
                    await auftragEntfernen(kopf.id);
                    // Nicht auffrischen — die Tour gibt es nicht mehr. Wer hier
                    // stehen bliebe, säße vor einem Bildschirm zu einer
                    // Kennung, die ins Leere zeigt.
                    gehZu('/disposition');
                  });
                }}
              >
                <p>
                  <strong>{kopf.nummer ?? kopf.referenz ?? 'Diese Tour'}</strong>{' '}
                  verschwindet vollständig — samt Fahrt und Verlauf. Das lässt
                  sich nicht rückgängig machen.
                </p>
                <p>
                  Gedacht ist das für Fehleingaben, etwa eine versehentlich
                  doppelt eingetragene Tour. Wurde die Tour <em>abgesagt</em>,
                  gehört sie storniert: Dann bleibt sie mit Begründung stehen
                  und ist in einem halben Jahr noch erklärbar.
                </p>
              </Rueckfrage>
            )}

            {stornoOffen && (
              <Rueckfrage
                titel="Warum wird diese Tour storniert?"
                bestaetigungstext="Endgültig stornieren"
                art="gefahr"
                laeuft={laeuft}
                bestaetigungGesperrt={stornogrund.trim().length < 3}
                abbrechen={() => {
                  setStornoOffen(false);
                  setStornogrund('');
                }}
                bestaetigen={() => {
                  const grund = stornogrund;
                  setStornoOffen(false);
                  setStornogrund('');
                  void mitFehlern(() => auftragStornieren(kopf.id, grund));
                }}
              >
                <Textfeld
                  marke="Grund"
                  wert={stornogrund}
                  setzen={setStornogrund}
                  autoFocus
                  platzhalter="z. B. Kunde hat kurzfristig abgesagt"
                  hinweis="Pflichtangabe. Eine Tour ohne Umsatz ist sonst in einem halben Jahr nicht mehr erklärbar."
                />
              </Rueckfrage>
            )}

            {/* -------------------------------- Lesen oder ändern, nie beides */}
            {bearbeiten && !gesperrt ? (
              <Eckdatenformular
                // An der Tour, nicht am Stand: Auffrischen gleicht ab, statt
                // die Eingaben wegzuwerfen (siehe `mitFehlern`, E-039). Doppelt
                // gemoppelt, weil die ganze Seite schon an der ID hängt — der
                // `key` hier sagt es an der Stelle, an der man ihn sucht.
                key={kopf.id}
                auftrag={kopf}
                fahrten={fahrten}
                kunden={kunden.daten ?? []}
                orte={orte.daten ?? []}
                fahrer={fahrer}
                wagen={wagen}
                laeuft={laeuft}
                abbrechen={() => setBearbeiten(false)}
                speichern={(auftragswerte, besetzungen) =>
                  void mitFehlern(async () => {
                    // Der Auftrag zuerst: Scheitert die Standprüfung, weil
                    // jemand anderes die Tour angefasst hat, ist noch keine
                    // Fahrt verändert. Andersherum stünde die halbe Änderung
                    // in den Daten und die andere Hälfte in einer Fehlermeldung.
                    if (Object.keys(auftragswerte).length > 0) {
                      await auftragAendern(kopf, auftragswerte);
                    }
                    for (const { fahrt, werte } of besetzungen) {
                      await fahrtBesetzen(fahrt, werte);
                    }
                    setBearbeiten(false);
                  })
                }
              />
            ) : (
              <Eckdatenliste auftrag={kopf} fahrten={fahrten} />
            )}

            {/* ══ DIE NOTIZ DES FAHRERS — NUR LESEN ══

                Getrennt von der Büronotiz weiter unten: Die eine schreibt das
                Büro, die andere kommt vom Fahrer. Das Büro schreibt nie in die
                Spalte des Fahrers, deshalb hat dieser Block kein Feld. Er steht
                in den Eckdaten und nicht erst bei der Freigabe, weil eine
                Standzeit auch bei der Nachfrage des Kunden eine Rolle spielt. */}
            <Fahrernotizen fahrten={fahrten} leute={leute.daten ?? []} />

            {/* Ohne Fahrt taucht die Tour bei keinem Fahrer auf — und vorher gab
                es hier keinen Weg, das zu beheben. */}
            {fahrten.length === 0 && !gesperrt && (
              <Meldung
                art="warnung"
                titel="Zu dieser Tour gibt es keine Fahrt"
                knopf={{
                  text: 'Fahrt nachtragen',
                  tun: () => void mitFehlern(async () => void (await fahrtNachtragen(kopf))),
                }}
              >
                Ohne Fahrt taucht sie bei keinem Fahrer auf. Die Fahrt entsteht
                am Ladedatum und ohne Fahrer und Fahrzeug — die trägst du danach
                unter „Bearbeiten“ ein.
              </Meldung>
            )}
          </div>
        </Karte>

        {kopf.art === 'pendel' && (
          <Relationenkarte
            key={strecken.map((r) => r.id).join('|')}
            auftrag={kopf}
            fahrten={fahrten}
            strecken={strecken}
            gesperrt={gesperrt}
            orte={orte.daten ?? []}
            vorlagen={(vorlagen.daten ?? [])
              .filter((v) => v.aktiv)
              .map((v) => ({
                id: v.id,
                text: `${v.bezeichnung ? `${v.bezeichnung} · ` : ''}${v.ladeort} → ${v.entladeort}`,
                ladeortId: v.ladeort_id,
                entladeortId: v.entladeort_id,
              }))}
            laeuft={laeuft}
            hinzufuegen={(ladeortId, entladeortId) =>
              void mitFehlern(async () => {
                // Steht am Auftrag noch keine Strecke, bekommt er die Orte der
                // ersten Relation — wie beim Eintragen. Zuerst der Auftrag:
                // Scheitert die Standprüfung, ist noch nichts angelegt.
                if (kopf.ladeort_id === null && kopf.entladeort_id === null) {
                  await auftragAendern(kopf, {
                    ladeort_id: ladeortId,
                    entladeort_id: entladeortId,
                  });
                }
                const fahrtId = fahrten[0]?.id ?? (await fahrtNachtragen(kopf));
                await streckeNachtragen(fahrtId, ladeortId, entladeortId);
              })
            }
          />
        )}

        <Belegkarte
          auftrag={kopf}
          dokumente={dokumente}
          gesperrt={gesperrt}
          offenerBeleg={bild?.beleg.id ?? null}
          auffrischen={detail.auffrischen}
          melden={setFehler}
          freigeben={(grund) =>
            void mitFehlern(() => belegeFreigeben(kopf.id, grund))
          }
          zuruecknehmen={() =>
            void mitFehlern(() => belegfreigabeZuruecknehmen(kopf.id))
          }
          verwerfen={(beleg) =>
            void mitFehlern(async () => {
              if (bild?.beleg.id === beleg.id) setBild(null);
              await dokumentVerwerfen(beleg);
            })
          }
          ansehen={async (beleg) => {
            setFehler(null);
            if (bild?.beleg.id === beleg.id) {
              setBild(null);
              return;
            }
            try {
              setBild({ beleg, adresse: await belegAdresse(beleg.storage_pfad) });
            } catch (problem) {
              setBild(null);
              setFehler(
                problem instanceof Speicherfehler
                  ? problem.klar
                  : alsKlarfehler(problem),
              );
            }
          }}
        />

        {bild && (
          <Karte
            titel={
              bild.beleg.dateiname ??
              BELEGART[bild.beleg.art as keyof typeof BELEGART] ??
              'Dokument'
            }
            werkzeuge={
              <div className="reihe" style={{ gap: 8 }}>
                {/* Der Speicher gibt die Adresse nur für fünf Minuten frei.
                    „Groß ansehen" ist deshalb kein Link zum Aufheben, sondern
                    der Weg, ein Foto groß und gedreht anzusehen. */}
                <a
                  className="knopf knopf--still knopf--klein"
                  href={bild.adresse}
                  target="_blank"
                  rel="noreferrer"
                >
                  Groß ansehen
                </a>
                <Knopf art="still" klein onClick={() => setBild(null)}>
                  Schließen
                </Knopf>
              </div>
            }
          >
            {bild.beleg.mimetype === 'application/pdf' ? (
              <iframe title="Dokument" className="belegblatt" src={bild.adresse} />
            ) : (
              <img
                className="belegbild"
                src={bild.adresse}
                alt={bild.beleg.dateiname ?? 'Dokument'}
              />
            )}
          </Karte>
        )}

        <Notizkarte
          auftragId={kopf.id}
          notiz={kopf.notiz}
          gesperrt={gesperrt}
          laeuft={laeuft}
          speichern={(text) => void mitFehlern(() => notizSetzen(kopf, text))}
        />

        <Verlaufskarte zeilen={verlauf} />
      </div>
    </>
  );
}

/* ------------------------------------------------------ Notiz des Fahrers */

/**
 * Was der Fahrer an der Fahrt notiert hat — je Fahrt ein Block.
 *
 * Bei EINER Fahrt genügt die Überschrift; bei mehreren (Freitag vorladen,
 * Montag zustellen) steht der Tag dabei, sonst weiß man nicht, zu welcher
 * Fahrt die Standzeit gehört. Gelesen wird aus `fahrt.bemerkung`, das die
 * Detailsicht ohnehin geladen hat — kein zweiter Weg zu denselben Daten.
 */
function Fahrernotizen(eigenschaften: {
  fahrten: Fahrtzeile[];
  leute: { id: string; vorname: string; nachname: string }[];
}) {
  const mitNotiz = eigenschaften.fahrten.filter(
    (f) => (f.bemerkung ?? '').trim() !== '',
  );
  if (mitNotiz.length === 0) return null;

  return (
    <div className="stapel" style={{ gap: 10 }}>
      {mitNotiz.map((fahrt) => {
        const fahrer = eigenschaften.leute.find((m) => m.id === fahrt.fahrer_id);
        const titel =
          eigenschaften.fahrten.length > 1
            ? `Notiz des Fahrers · ${datum(fahrt.datum)}${
                fahrer ? ` · ${fahrer.vorname}` : ''
              }`
            : 'Notiz des Fahrers';
        return (
          <Fahrernotizblock
            key={fahrt.id}
            titel={titel}
            zeilen={[(fahrt.bemerkung ?? '').trim()]}
          />
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------------- Relationen */

/**
 * Die Strecken einer Pendeltour — und der Weg, sie nachzutragen.
 *
 * ═══ WARUM ES DIESEN WEG GIBT ═══
 *
 * Eine Pendeltour ohne Relation entstand, weil das Eintragen-Formular sie nicht
 * verlangte: Der Fahrer sah „Noch keine Relation erfasst" und keinen Knopf,
 * etwas hinzuzufügen. Ohne einen Weg im Büro ließe sich so eine Tour nie
 * reparieren — es bliebe nur ein zweiter Auftrag, und dann stünden beide in der
 * Auswertung.
 *
 * Nachtragen geht nur, solange noch keine Fuhre gezählt ist. Danach hat der
 * Fahrer schon gegen die vorhandenen Strecken gearbeitet; eine neue würde die
 * Zählung nicht mehr erklären.
 */
function Relationenkarte(eigenschaften: {
  auftrag: Auftragszeile;
  fahrten: Fahrtzeile[];
  strecken: Streckenzeile[];
  gesperrt: boolean;
  orte: { id: string; name: string; aktiv: boolean; strasse: string | null; plz: string | null; ortsname: string | null }[];
  vorlagen: { id: string; text: string; ladeortId: string; entladeortId: string }[];
  laeuft: boolean;
  hinzufuegen: (ladeortId: string, entladeortId: string) => void;
}) {
  const [ladeortId, setLadeortId] = useState<string | null>(null);
  const [entladeortId, setEntladeortId] = useState<string | null>(null);
  const [vorlagenOffen, setVorlagenOffen] = useState(false);

  const ortsname = (id: string | null) =>
    eigenschaften.orte.find((o) => o.id === id)?.name ?? '—';
  const auswahl = eigenschaften.orte
    .filter((o) => o.aktiv)
    .map((o) => ({
      id: o.id,
      name: o.name,
      unter: anschriftszeile(o.strasse, o.plz, o.ortsname),
    }));

  const nachtragbar = !eigenschaften.gesperrt && Number(eigenschaften.auftrag.fuhren ?? 0) === 0;

  return (
    <Karte titel={`Relationen (${eigenschaften.strecken.length})`}>
      <div className="stapel" data-relationenkarte>
        {eigenschaften.strecken.length === 0 ? (
          <Meldung art="warnung" titel="Noch keine Relation erfasst">
            Ohne Relation kann der Fahrer keine Fuhre zählen. Trag hier mindestens
            eine ein.
          </Meldung>
        ) : (
          <ul className="fehltliste" style={{ listStyle: 'none', paddingLeft: 0 }}>
            {eigenschaften.strecken.map((r) => (
              <li key={r.id}>
                <strong>
                  {ortsname(r.ladeort_id)} → {ortsname(r.entladeort_id)}
                </strong>{' '}
                <span className="meta">
                  · {r.anzahl} {r.anzahl === 1 ? 'Fuhre' : 'Fuhren'}
                </span>
              </li>
            ))}
          </ul>
        )}

        {nachtragbar ? (
          <div className="stapel">
            <div className="reihe" style={{ justifyContent: 'space-between' }}>
              <span className="feld__marke">Relation hinzufügen</span>
              {eigenschaften.vorlagen.length > 0 && (
                <Knopf art="zweit" klein onClick={() => setVorlagenOffen((o) => !o)}>
                  Aus Streckenvorlage
                </Knopf>
              )}
            </div>
            {vorlagenOffen && (
              <div className="stapel" style={{ gap: 6 }}>
                {eigenschaften.vorlagen.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    className="wahlzeile"
                    onClick={() => {
                      setLadeortId(v.ladeortId);
                      setEntladeortId(v.entladeortId);
                      setVorlagenOffen(false);
                    }}
                  >
                    <span>{v.text}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="feldreihe">
              <Auswahlsuche marke="Relation: Von" wert={ladeortId} setzen={setLadeortId} auswahl={auswahl} />
              <Auswahlsuche marke="Relation: Nach" wert={entladeortId} setzen={setEntladeortId} auswahl={auswahl} />
            </div>
            <div className="reihe reihe--rechts">
              <Knopf
                art="haupt"
                disabled={eigenschaften.laeuft || ladeortId === null || entladeortId === null}
                onClick={() => {
                  if (ladeortId && entladeortId) eigenschaften.hinzufuegen(ladeortId, entladeortId);
                }}
              >
                Relation hinzufügen
              </Knopf>
            </div>
          </div>
        ) : (
          !eigenschaften.gesperrt && (
            <p className="meta">
              Es ist schon eine Fuhre gezählt — Relationen lassen sich jetzt nicht
              mehr nachtragen.
            </p>
          )
        )}
      </div>
    </Karte>
  );
}

/* ------------------------------------------------------------ Eckdaten */

function Eck(eigenschaften: {
  marke: string;
  wert: string | null;
  /** Zweite, kleinere Zeile — bei Orten die Postleitzahl und der Ort. */
  unter?: string | null;
  /**
   * Was hier stehen soll, wenn nichts da ist. Gesetzt heißt: Das Feld bleibt
   * sichtbar, auch leer — weil sein Fehlen eine Aufgabe ist. Ohne diesen Wert
   * verschwindet das Feld ganz.
   */
  fehlt?: string;
}) {
  if (!eigenschaften.wert && !eigenschaften.fehlt) return null;

  return (
    <div className="eckdaten__paar">
      <dt>{eigenschaften.marke}</dt>
      <dd>
        {eigenschaften.wert ?? (
          <span className="eckdaten__fehlt">{eigenschaften.fehlt}</span>
        )}
        {eigenschaften.wert && eigenschaften.unter && (
          <span className="eckdaten__unterzeile">{eigenschaften.unter}</span>
        )}
      </dd>
    </div>
  );
}

/** „72458 Albstadt" — was von der Anschrift eines Orts hierher gehört. */
function ortsunterzeile(plz: string | null, ortsname: string | null): string | null {
  const teile = [plz, ortsname].filter(Boolean);
  return teile.length > 0 ? teile.join(' ') : null;
}

/** Preis und Preisart in einer Zeile — „Nach Menge · 12,50 € je Tonne". */
function preistext(auftrag: Auftragszeile): string | null {
  if (!auftrag.preisart) return null;
  const art = PREISARTTEXT[auftrag.preisart] ?? auftrag.preisart;
  if (auftrag.preis_vk === null) return `${art} · Preis fehlt`;
  if (auftrag.preisart === 'menge') {
    const einheit =
      EINHEITTEXT[auftrag.mengeneinheit ?? ''] ?? auftrag.mengeneinheit ?? 'Einheit';
    return `${art} · ${euro(Number(auftrag.preis_vk))} je ${einheit}`;
  }
  if (auftrag.preisart === 'stundensatz') {
    return `${art} · ${euro(Number(auftrag.preis_vk))} je Stunde`;
  }
  return `${art} · ${euro(Number(auftrag.preis_vk))}`;
}

/** „6 Fuhren · 148 t" — was davon da ist. */
function mengentext(auftrag: Auftragszeile): string | null {
  const teile: string[] = [];
  if (auftrag.fuhren > 0) {
    teile.push(`${auftrag.fuhren} ${auftrag.fuhren === 1 ? 'Fuhre' : 'Fuhren'}`);
  }
  if (auftrag.menge !== null) {
    teile.push(
      `${Number(auftrag.menge).toLocaleString('de-DE')} ${
        auftrag.mengeneinheit ?? ''
      }`.trim(),
    );
  }
  return teile.length > 0 ? teile.join(' · ') : null;
}

/**
 * „Do., 21.08.2026 gefahren · Fr., 22.08.2026 offen"
 *
 * ═══ WARUM DAS MEISTENS GAR NICHT ERSCHEINT ═══
 *
 * Ladedatum und Fahrtag sind Soll und Ist. Das Ladedatum ist die Planung des
 * Büros und hängt am Auftrag; der Fahrtag hängt an der Fahrt und sagt, wann
 * tatsächlich gefahren wurde. In neun von zehn Fällen ist das derselbe Tag —
 * und dann stand hier dasselbe Datum ein zweites Mal, unter einem anderen
 * Namen. Aus dem Büro kam dazu die Frage, was denn der Unterschied sei.
 *
 * Also: Die Zeile erscheint nur, wenn sie etwas Neues sagt — wenn ein Fahrtag
 * vom Ladedatum abweicht oder wenn es mehr als eine Fahrt gibt (Freitag
 * vorladen, Montag zustellen). Sonst genügt das Ladedatum oben.
 */
function fahrtentext(
  fahrten: Fahrtzeile[],
  ladedatum: string | null,
): string | null {
  if (fahrten.length === 0) return null;

  const weichtAb =
    fahrten.length > 1 || fahrten.some((f) => f.datum !== ladedatum);
  if (!weichtAb) return null;

  return fahrten
    .map(
      (f) =>
        `${datum(f.datum)} ${f.status === 'abgeschlossen' ? 'gefahren' : 'offen'}`,
    )
    .join(' · ');
}

/**
 * Ein Abschnitt der Eckdaten. Eine leere Überschrift bleibt nicht stehen.
 *
 * ═══ WARUM HIER IN DIE EIGENSCHAFTEN GESCHAUT WIRD ═══
 *
 * Der erste Versuch zählte die Kinder und ließ den Abschnitt weg, wenn keine
 * da waren. Das griff nicht: Ein `<Eck>` ist auch dann ein Kind, wenn es
 * `null` zurückgibt, weil sein Wert fehlt. Übrig blieb eine Überschrift ohne
 * alles — „AUFTRAG" und darunter Luft.
 *
 * Also wird gefragt, ob ein Kind überhaupt etwas anzuzeigen HAT: einen Wert
 * oder einen Text für den Fall, dass er fehlt. Das ist dieselbe Bedingung, die
 * `Eck` selbst benutzt — sie steht bewusst an beiden Stellen gleich, statt das
 * Ergebnis zu raten.
 */
function Eckgruppe(eigenschaften: { titel: string; children: React.ReactNode }) {
  const inhalt = React.Children.toArray(eigenschaften.children).filter(
    (kind) =>
      React.isValidElement<{ wert?: string | null; fehlt?: string }>(kind) &&
      Boolean(kind.props.wert || kind.props.fehlt),
  );
  if (inhalt.length === 0) return null;
  return (
    <section className="eckgruppe">
      <div className="eckgruppe__titel">{eigenschaften.titel}</div>
      <dl className="eckblock">{inhalt}</dl>
    </section>
  );
}

/**
 * Die Eckdaten im Lesezustand.
 *
 * ═══ VIER BLÖCKE STATT FÜNFZEHN KACHELN ═══
 *
 * Vorher standen alle Angaben gleichrangig in einem Raster. Bei einer frisch
 * angelegten Tour zeigte die Hälfte davon nur einen Strich, und wer etwas
 * suchte, musste fünfzehn Beschriftungen lesen.
 *
 * Jetzt beantwortet jeder Block eine Frage — wer und wann, wohin, wer fährt,
 * was kostet es. Man springt zur Frage statt zur Beschriftung.
 *
 * ═══ LEERE FELDER VERSCHWINDEN — BIS AUF VIER ═══
 *
 * Material, Menge, Kennung und Herkunft blendet ein leeres Feld aus. Sichtbar
 * bleiben Ladedatum, Fahrer, Fahrzeug und Preis: Deren Fehlen ist keine
 * Leerstelle, sondern eine Aufgabe. Sie stehen dann als „fehlt noch" da, nicht
 * als Strich — ein Strich sagt „hier ist nichts", und das ist etwas anderes.
 */
function Eckdatenliste(eigenschaften: {
  auftrag: Auftragszeile;
  fahrten: Fahrtzeile[];
}) {
  const { auftrag } = eigenschaften;
  const summe = betrag(auftrag);

  return (
    // Die Klasse trägt keine Gestaltung mehr — sie ist der Anker, an dem die
    // Prüfung erkennt, dass die Detailsicht steht. Ohne sie müsste jeder der
    // betroffenen Schritte einen neuen Namen kennen.
    <div className="eckdaten">
      <Eckgruppe titel="Auftrag">
        {/* Kunde und Ladedatum stehen bereits groß in der Kopfzeile. Sie hier
            zu wiederholen wäre genau die Doppelung, die den Bildschirm
            unübersichtlich gemacht hat. */}
        <Eck
          marke={
            auftrag.externe_nummer ? 'Auftragsnummer des Kunden' : 'Kennung (gebildet)'
          }
          wert={auftrag.referenz}
        />
      </Eckgruppe>

      {/* Von und Nach gehören zusammen und haben eine Richtung — deshalb eine
          Zeile mit Pfeil und nicht zwei Kacheln nebeneinander. */}
      <section className="eckgruppe">
        <div className="eckgruppe__titel">Strecke</div>
        <div className="eckstrecke">
          <dl className="eckdaten__paar">
            <Eck
              marke="Von"
              wert={auftrag.ladeort}
              unter={ortsunterzeile(auftrag.ladeort_plz, auftrag.ladeort_ortsname)}
              fehlt="fehlt noch"
            />
          </dl>
          <span className="eckstrecke__pfeil" aria-hidden="true">
            →
          </span>
          <dl className="eckdaten__paar">
            <Eck
              marke="Nach"
              wert={auftrag.entladeort}
              unter={ortsunterzeile(
                auftrag.entladeort_plz,
                auftrag.entladeort_ortsname,
              )}
              fehlt="fehlt noch"
            />
          </dl>
        </div>
        {/* Der Kasten selbst verschwindet, wenn beide Angaben fehlen — sonst
            hielte er einen Streifen Leerraum frei, den niemand füllen wird. */}
        {(auftrag.material || mengentext(auftrag)) && (
          <dl className="eckblock" style={{ marginTop: 14 }}>
            <Eck marke="Material" wert={auftrag.material} />
            <Eck marke="Menge" wert={mengentext(auftrag)} />
          </dl>
        )}
      </section>

      <Eckgruppe titel="Besetzung">
        <Eck marke="Fahrer" wert={auftrag.fahrer || null} fehlt="noch offen" />
        <Eck marke="Fahrzeug" wert={auftrag.fahrzeug || null} fehlt="noch offen" />
        {/* „Gefahren am", nicht „Fahrtage": Damit steht Geplant gegen
            Gefahren, und der Unterschied erklärt sich von selbst. */}
        <Eck
          marke="Gefahren am"
          wert={fahrtentext(eigenschaften.fahrten, auftrag.ladedatum)}
        />
      </Eckgruppe>

      <Eckgruppe titel="Preis">
        {/* „Vereinbart", nicht noch einmal „Preis" — die Blocküberschrift sagt
            das schon. Zweimal dasselbe Wort untereinander liest sich wie ein
            Fehler. */}
        <Eck marke="Vereinbart" wert={preistext(auftrag)} fehlt="fehlt noch" />
        <Eck
          marke="Betrag netto"
          wert={
            summe !== null
              ? euro(summe)
              : auftrag.preisart === 'stundensatz'
                ? 'nach Stunden des Fahrers'
                : null
          }
        />
      </Eckgruppe>

      {/* Wer die Tour angelegt hat, ist eine Randnotiz und kein Eckdatum. Als
          eigene Kachel unter „Auftrag" stand sie gleichrangig neben Kunde und
          Strecke — und war meistens das Einzige, was dort noch übrig war. */}
      <p className="meta" style={{ marginTop: 18 }}>
        Angelegt {auftrag.herkunft === 'fahrer' ? 'vom Fahrer (App)' : 'im Büro'} ·{' '}
        {zeitpunkt(auftrag.erstellt_am)}
      </p>
    </div>
  );
}

/* -------------------------------------------------------- Eckdaten ändern */

/** Felder, die als Zahl verglichen werden — „12,50" ist dasselbe wie 12.5. */
const ZAHLENFELDER = { zahlen: ['preis_vk', 'abrechnungsmenge'] };

const ARTTEXT: Record<string, string> = {
  standard: 'Standard',
  pendel: 'Pendelverkehr',
  sonstige: 'Sonstige',
  fremdvergabe: 'Fremdvergabe',
};

/**
 * Die Eckdaten als flache Felder, so wie sie im Formular stehen: Text, wie man
 * ihn tippt, Kennungen für Auswahlen, und je Fahrt zwei Felder für die
 * Besetzung. Daraus entstehen Ausgangsstand und frischer Stand.
 */
function eckdatenAlsFelder(auftrag: Auftragszeile, fahrten: Fahrtzeile[]): Felder {
  const felder: Felder = {
    art: auftrag.art,
    kunde_id: auftrag.kunde_id,
    ladeort_id: auftrag.ladeort_id,
    entladeort_id: auftrag.entladeort_id,
    ladedatum: auftrag.ladedatum ?? '',
    material: auftrag.material ?? '',
    externe_nummer: auftrag.externe_nummer ?? '',
    preisart: auftrag.preisart ?? '',
    preis_vk: zahlAlsText(auftrag.preis_vk),
    mengeneinheit: auftrag.mengeneinheit ?? 't',
    abrechnungsmenge: zahlAlsText(auftrag.abrechnungsmenge),
  };
  for (const fahrt of fahrten) {
    felder[`fahrer:${fahrt.id}`] = fahrt.fahrer_id;
    felder[`fahrzeug:${fahrt.id}`] = fahrt.fahrzeug_id;
  }
  return felder;
}

/**
 * Dieselben Eckdaten, nur änderbar — und in EINEM Formular.
 *
 * ═══ WARUM ALLES IN EINEM SPEICHERVORGANG ═══
 *
 * Vorher hatte der Preis seinen eigenen Speicherknopf, die Fahrerzuordnung
 * speicherte sofort beim Umstellen und der Rest hatte einen dritten Knopf. Wer
 * Datum, Preis und Fahrer korrigierte, löste drei Vorgänge aus — und wenn der
 * zweite scheiterte, stand die Hälfte in den Daten. Jetzt ist ein Klick ein
 * Vorgang.
 *
 * ═══ AUSGANGSSTAND STATT NEUAUFBAU (H1.3b, E-039) ═══
 *
 * Das Formular merkt sich die Werte beim Öffnen. Gespeichert wird nur, was
 * davon abweicht — gegen den AKTUELLEN Stand (`geaendert_am` der frischen
 * Props). Frischt die Seite auf, während das Formular offen ist (Notiz,
 * Beleg, Konflikt), gleicht `abgleichen()` ab: Nicht angefasste Felder folgen
 * still, eigene Eingaben bleiben. Hat ein Kollege ein Feld geändert, das man
 * selbst geändert hat, steht sein Wert am Feld, und Speichern ist gesperrt,
 * bis man sich entschieden hat. So dreht ein zweites Speichern nach einem
 * Konflikt nie eine fremde Änderung still zurück.
 *
 * ═══ WARUM DIE MENGENEINHEIT NICHT GELEERT WIRD ═══
 *
 * Sie beschreibt die MENGE, nicht den Preis: 148 t bleiben 148 t, auch wenn die
 * Tour pauschal abgerechnet wird. Vorher setzte das Speichern des Preises sie
 * bei jeder anderen Preisart auf null — danach stand in den Eckdaten „148" ohne
 * Einheit, und auf dem Leistungsnachweis eine leere Einheitenspalte.
 */
function Eckdatenformular(eigenschaften: {
  auftrag: Auftragszeile;
  fahrten: Fahrtzeile[];
  kunden: {
    id: string;
    name: string;
    aktiv: boolean;
    strasse: string | null;
    plz: string | null;
    ort: string | null;
  }[];
  /** Die Anschrift gehört dazu: Sie ist das, woran zwei Standorte desselben
   *  Betriebs auseinandergehen. */
  orte: {
    id: string;
    name: string;
    aktiv: boolean;
    strasse: string | null;
    plz: string | null;
    ortsname: string | null;
  }[];
  fahrer: Fahrerwahl[];
  wagen: { id: string; kennzeichen: string; kurzname: string | null }[];
  laeuft: boolean;
  abbrechen: () => void;
  speichern: (
    auftragswerte: Record<string, unknown>,
    besetzungen: {
      fahrt: Fahrtzeile;
      werte: { fahrer_id: string | null; fahrzeug_id: string | null };
    }[],
  ) => void;
}) {
  const { auftrag, fahrten } = eigenschaften;

  // ═══ DER FRISCHE STAND FLIESST HINEIN, OHNE DIE EINGABEN ZU ERSETZEN ═══
  //
  // Verglichen wird über den Text der Felder, nicht über die Objekte: Die
  // Seite reicht bei jedem Rendern neue Objekte herein, auch ohne neuen Stand.
  // Abgeglichen wird direkt beim Rendern (nicht in einem Effekt), damit nie
  // ein Bild mit altem Stand und neuen Props entsteht.
  const frisch = eckdatenAlsFelder(auftrag, fahrten);
  const frischSchluessel = JSON.stringify(frisch);
  const [stand, setStand] = useState<Formularstand>(() => ({
    ausgang: frisch,
    eingaben: frisch,
    konflikte: [],
  }));
  const [zuletzt, setZuletzt] = useState(frischSchluessel);
  if (zuletzt !== frischSchluessel) {
    setZuletzt(frischSchluessel);
    setStand((bisher) =>
      abgleichen(bisher.ausgang, bisher.eingaben, frisch, ZAHLENFELDER, bisher.konflikte),
    );
  }

  const e = stand.eingaben;
  const setze = (feld: string) => (wert: string | null) =>
    setStand((bisher) => eingeben(bisher, feld, wert, ZAHLENFELDER));

  const art = e.art ?? 'standard';
  const preisart = e.preisart ?? '';
  const einheit = e.mengeneinheit ?? 't';
  const preis = e.preis_vk ?? '';
  const menge = e.abrechnungsmenge ?? '';

  const eigene = eigeneAenderungen(stand.ausgang, stand.eingaben, ZAHLENFELDER);

  // Verglichen wird, was die Texte BEDEUTEN, nicht wie sie aussehen (das macht
  // `eigeneAenderungen`). Steht Unsinn im Feld, wird nichts geschrieben —
  // `preisUngueltig` sperrt außerdem den Knopf.
  const preisGelesen = zahlLesen(preis);
  const mengeSichtbar = preisart === 'menge' && einheit !== 'fuhre';
  const mengeGelesen = zahlLesen(menge);

  const auftragswerte: Record<string, unknown> = {};
  for (const [feld, wert] of Object.entries(eigene)) {
    if (feld.startsWith('fahrer:') || feld.startsWith('fahrzeug:')) continue;
    if (feld === 'ladedatum' || feld === 'preisart') {
      auftragswerte[feld] = wert || null;
    } else if (feld === 'material' || feld === 'externe_nummer') {
      auftragswerte[feld] = wert?.trim() || null;
    } else if (feld === 'preis_vk') {
      if (preisGelesen.art !== 'ungueltig') {
        auftragswerte.preis_vk = preisGelesen.art === 'leer' ? null : preisGelesen.wert;
      }
    } else if (feld === 'abrechnungsmenge') {
      /**
       * ═══ DIE MENGE, NACH DER ABGERECHNET WIRD ═══
       *
       * Der Preis war da, der Betrag „—": Preis × Menge, und die Menge kam
       * bisher nur aus den Fuhrenblöcken. Ein Standardauftrag hat keine, und
       * das Wiegegewicht ist Sache des Büros. Bei „je Fuhre" zählt die
       * Fuhrenzahl — dort gibt es kein Feld, das etwas anderes behauptet.
       */
      if (mengeSichtbar && mengeGelesen.art !== 'ungueltig') {
        auftragswerte.abrechnungsmenge =
          mengeGelesen.art === 'leer' ? null : mengeGelesen.wert;
      }
    } else if (feld !== 'mengeneinheit') {
      auftragswerte[feld] = wert;
    }
  }
  // Nur bei „nach Menge" wird die Einheit überhaupt angeboten — und nur dann
  // geschrieben: wenn sie geändert wurde, oder wenn die Tour noch gar keine
  // hat (sonst stünde ein Mengenpreis ohne Einheit da). Von einer
  // Preisänderung wird sie nicht mit weggeräumt.
  if (preisart === 'menge' && ('mengeneinheit' in eigene || auftrag.mengeneinheit === null)) {
    auftragswerte.mengeneinheit = einheit;
  }

  const besetzungen = fahrten
    .filter((f) => `fahrer:${f.id}` in eigene || `fahrzeug:${f.id}` in eigene)
    .map((f) => ({
      fahrt: f,
      werte: {
        fahrer_id: e[`fahrer:${f.id}`] ?? null,
        fahrzeug_id: e[`fahrzeug:${f.id}`] ?? null,
      },
    }));

  const geaendert =
    Object.keys(auftragswerte).length > 0 || besetzungen.length > 0;

  const preisUngueltig =
    preisGelesen.art === 'ungueltig' ||
    (mengeSichtbar && mengeGelesen.art === 'ungueltig');

  /**
   * ═══ NUR SICHTBARE FELDER KÖNNEN SPERREN ═══
   *
   * Einheit und Menge verschwinden, je nach Preisart und Einheit — und was
   * nicht zu sehen ist, wird auch nicht geschrieben (siehe oben). Ein
   * Konflikt dort wäre einer, den man nirgends lösen kann: Speichern bliebe
   * gesperrt, und die Meldung spräche von markierten Feldern, die es nicht
   * gibt. Er bleibt deshalb im Stand und erscheint wieder, sobald das Feld
   * wieder sichtbar ist.
   */
  const sichtbar = (feld: string) =>
    feld === 'mengeneinheit'
      ? preisart === 'menge'
      : feld === 'abrechnungsmenge'
        ? mengeSichtbar
        : true;
  const offeneKonflikte = stand.konflikte.filter((k) => sichtbar(k.feld));
  const konfliktOffen = offeneKonflikte.length > 0;

  /**
   * Orte mit ihrer unterscheidenden zweiten Zeile — dieselbe wie beim
   * Eintragen. „Korn Recycling" gibt es in Albstadt UND in Ulm; mit dem Namen
   * allein wählt man hier genauso blind wie dort.
   */
  const ortsauswahl = eigenschaften.orte
    .filter((o) => o.aktiv)
    .map((o) => ({
      id: o.id,
      name: o.name,
      unter: anschriftszeile(o.strasse, o.plz, o.ortsname),
    }));

  /** Der fremde Wert so, wie man ihn liest — nicht als Kennung. */
  function anzeige(feld: string, wert: string | null): string {
    if (wert === null || wert.trim() === '') return 'leer';
    if (feld === 'art') return ARTTEXT[wert] ?? wert;
    if (feld === 'kunde_id') return eigenschaften.kunden.find((k) => k.id === wert)?.name ?? wert;
    if (feld === 'ladeort_id' || feld === 'entladeort_id') {
      return eigenschaften.orte.find((o) => o.id === wert)?.name ?? wert;
    }
    if (feld === 'ladedatum') {
      return new Date(wert + 'T12:00:00').toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }
    if (feld === 'preisart') return PREISARTTEXT[wert as keyof typeof PREISARTTEXT] ?? wert;
    if (feld === 'mengeneinheit') return EINHEITTEXT[wert as keyof typeof EINHEITTEXT] ?? wert;
    if (feld.startsWith('fahrer:')) {
      return eigenschaften.fahrer.find((f) => f.id === wert)?.text ?? 'jemand außerhalb der Auswahl';
    }
    if (feld.startsWith('fahrzeug:')) {
      const w = eigenschaften.wagen.find((x) => x.id === wert);
      return w ? (w.kurzname ?? w.kennzeichen) : 'ein Fahrzeug außerhalb der Auswahl';
    }
    return wert;
  }

  const hinweisId = (feld: string) => `konflikt-${auftrag.id}-${feld.replace(':', '-')}`;
  const konflikt = (feld: string) => offeneKonflikte.find((k) => k.feld === feld);
  /** `aria-describedby` am Feld, solange dort ein Konflikt steht. */
  const beschrieben = (feld: string) => (konflikt(feld) ? hinweisId(feld) : undefined);
  function hinweis(feld: string) {
    const k = konflikt(feld);
    if (!k) return null;
    return (
      <Konflikthinweis
        id={hinweisId(feld)}
        wert={anzeige(feld, k.frischerWert)}
        uebernehmen={() => setStand((bisher) => fremdUebernehmen(bisher, feld))}
        behalten={() => setStand((bisher) => meinsBehalten(bisher, feld))}
      />
    );
  }

  return (
    <div className="stapel">
      {/* Steht von Anfang an da, damit Vorleseprogramme die Änderung ansagen. */}
      <p className="nurvorlesen" role="status">
        {konfliktOffen
          ? `${offeneKonflikte.length === 1 ? 'Ein Feld wurde' : `${offeneKonflikte.length} Felder wurden`} inzwischen von jemand anderem geändert. Entscheide am Feld, bevor du speicherst.`
          : ''}
      </p>
      {konfliktOffen && (
        <Meldung art="warnung" titel="Jemand hat inzwischen dieselben Felder geändert">
          An den markierten Feldern steht, was jetzt gespeichert ist. Entscheide
          je Feld, ob du den neuen Wert übernimmst oder deinen behältst — danach
          lässt sich speichern.
        </Meldung>
      )}

      <div className="feldreihe">
        <Feld marke="Art">
          <select
            value={art}
            aria-describedby={beschrieben('art')}
            onChange={(ereignis) => setze('art')(ereignis.target.value)}
          >
            <option value="standard">Standard</option>
            <option value="pendel">Pendelverkehr</option>
            <option value="sonstige">Sonstige</option>
            <option value="fremdvergabe">Fremdvergabe</option>
          </select>
          {hinweis('art')}
        </Feld>

        <Auswahlsuche
          marke="Kunde"
          wert={e.kunde_id ?? null}
          setzen={setze('kunde_id')}
          auswahl={eigenschaften.kunden
            .filter((k) => k.aktiv)
            .map((k) => ({
              id: k.id,
              name: k.name,
              unter: anschriftszeile(k.strasse, k.plz, k.ort),
            }))}
          beschriebenVon={beschrieben('kunde_id')}
          zusatz={hinweis('kunde_id')}
        />

        <Feld
          marke="Ladedatum"
          hinweis={'Verschiebt auch, wann die Tour als „geplant“ oder „offen“ gilt.'}
        >
          <input
            type="date"
            value={e.ladedatum ?? ''}
            aria-describedby={beschrieben('ladedatum')}
            onChange={(ereignis) => setze('ladedatum')(ereignis.target.value)}
          />
          {hinweis('ladedatum')}
        </Feld>
      </div>

      <div className="feldreihe">
        <Auswahlsuche
          marke="Von"
          wert={e.ladeort_id ?? null}
          setzen={setze('ladeort_id')}
          auswahl={ortsauswahl}
          beschriebenVon={beschrieben('ladeort_id')}
          zusatz={hinweis('ladeort_id')}
        />
        <Auswahlsuche
          marke="Nach"
          wert={e.entladeort_id ?? null}
          setzen={setze('entladeort_id')}
          auswahl={ortsauswahl}
          beschriebenVon={beschrieben('entladeort_id')}
          zusatz={hinweis('entladeort_id')}
        />
      </div>

      <div className="feldreihe">
        <Feld marke="Material">
          <input
            type="text"
            value={e.material ?? ''}
            aria-describedby={beschrieben('material')}
            onChange={(ereignis) => setze('material')(ereignis.target.value)}
          />
          {hinweis('material')}
        </Feld>
        <Feld
          marke="Auftragsnummer des Kunden"
          hinweis={
            art === 'pendel'
              ? 'Bleibt sie leer, bildet das System eine Kennung aus Kunde, Datum und Kennzeichen.'
              : undefined
          }
        >
          <input
            type="text"
            value={e.externe_nummer ?? ''}
            aria-describedby={beschrieben('externe_nummer')}
            onChange={(ereignis) => setze('externe_nummer')(ereignis.target.value)}
          />
          {hinweis('externe_nummer')}
        </Feld>
      </div>

      {/* ------------------------------------------------------------ Preis */}
      <div className="feldreihe">
        <Feld
          marke="Preisart"
          hinweis="Pauschale je Tour, nach Menge oder Stundensatz."
        >
          <select
            value={preisart}
            aria-describedby={beschrieben('preisart')}
            onChange={(ereignis) => setze('preisart')(ereignis.target.value)}
          >
            <option value="">Noch offen</option>
            {Object.entries(PREISARTTEXT).map(([wert, text]) => (
              <option key={wert} value={wert}>
                {text}
              </option>
            ))}
          </select>
          {hinweis('preisart')}
        </Feld>

        <Feld
          marke={preisart === 'menge' ? 'Preis je Einheit (€)' : 'Verkaufspreis (€)'}
          hinweis={
            preisart === 'stundensatz'
              ? 'Der Betrag ergibt sich erst aus den Stunden des Fahrers.'
              : 'Netto, ohne Umsatzsteuer.'
          }
        >
          <Zahlfeld
            wert={preis}
            setzen={setze('preis_vk')}
            beschriftung="Verkaufspreis"
            beschriebenVon={beschrieben('preis_vk')}
          />
          {hinweis('preis_vk')}
        </Feld>

        {/* ═══ OHNE EINHEIT IST EIN MENGENPREIS KEINE ANGABE ═══

            12,50 € — je Tonne oder je Fuhre? Bei sechs Fuhren zu 148 Tonnen ist
            das der Faktor 25, und der Unterschied wirkt still: `betrag()`
            rechnet bei „Fuhre" mit der Fuhrenzahl und sonst mit der Menge. Am
            Ende steht einfach eine andere Zahl auf der Rechnung. */}
        {preisart === 'menge' && (
          <Feld marke="Je Einheit" hinweis="Entscheidet, womit gerechnet wird.">
            <select
              value={einheit}
              aria-describedby={beschrieben('mengeneinheit')}
              onChange={(ereignis) => setze('mengeneinheit')(ereignis.target.value)}
            >
              {Object.entries(EINHEITTEXT).map(([wert, text]) => (
                <option key={wert} value={wert}>
                  {text}
                </option>
              ))}
            </select>
            {hinweis('mengeneinheit')}
          </Feld>
        )}

        {mengeSichtbar && (
          <Feld
            marke={mengenmarke(einheit)}
            hinweis="Die Menge, nach der abgerechnet wird — z. B. laut Wiegeschein. Leer heißt: es zählt, was der Fahrer erfasst hat."
          >
            <Zahlfeld
              wert={menge}
              setzen={setze('abrechnungsmenge')}
              beschriftung={mengenmarke(einheit)}
              beschriebenVon={beschrieben('abrechnungsmenge')}
            />
            {hinweis('abrechnungsmenge')}
          </Feld>
        )}
      </div>

      {/* --------------------------------------------------------- Wer fährt */}
      {fahrten.length === 0 ? (
        <p className="stumm">
          Zu dieser Tour gibt es keine Fahrt — deshalb ist hier niemand
          zuzuordnen.
        </p>
      ) : (
        fahrten.map((fahrt) => (
          <div className="feldreihe" key={fahrt.id}>
            <Feld
              marke={`Fahrer am ${datum(fahrt.datum)}`}
              hinweis={
                fahrt.status === 'abgeschlossen' ? 'Fahrt gefahren' : 'Fahrt offen'
              }
            >
              <select
                value={e[`fahrer:${fahrt.id}`] ?? ''}
                aria-describedby={beschrieben(`fahrer:${fahrt.id}`)}
                onChange={(ereignis) =>
                  setze(`fahrer:${fahrt.id}`)(ereignis.target.value || null)
                }
              >
                <option value="">Noch offen</option>
                {eigenschaften.fahrer.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.text}
                  </option>
                ))}
              </select>
              {hinweis(`fahrer:${fahrt.id}`)}
            </Feld>

            <Feld marke="Fahrzeug">
              <select
                value={e[`fahrzeug:${fahrt.id}`] ?? ''}
                aria-describedby={beschrieben(`fahrzeug:${fahrt.id}`)}
                onChange={(ereignis) =>
                  setze(`fahrzeug:${fahrt.id}`)(ereignis.target.value || null)
                }
              >
                <option value="">Noch offen</option>
                {eigenschaften.wagen.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.kurzname ?? w.kennzeichen}
                  </option>
                ))}
              </select>
              {hinweis(`fahrzeug:${fahrt.id}`)}
            </Feld>
          </div>
        ))
      )}

      <div className="reihe reihe--rechts">
        <Knopf art="still" onClick={eigenschaften.abbrechen}>
          {geaendert ? 'Änderungen verwerfen' : 'Schließen'}
        </Knopf>
        <Knopf
          art="haupt"
          disabled={!geaendert || preisUngueltig || konfliktOffen || eigenschaften.laeuft}
          onClick={() => eigenschaften.speichern(auftragswerte, besetzungen)}
        >
          Speichern
        </Knopf>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- Dokumente */

/**
 * Beleglage und Dokumente in EINER Karte.
 *
 * ═══ WARUM SIE NICHT GETRENNT SIND ═══
 *
 * Sie beantworteten dieselbe Frage von zwei Seiten: „Ist alles da?" und „Was
 * ist da?". Nebeneinander gestellt las man erst „Ausgang fehlt" und darunter
 * eine Kachelreihe, in der man selbst nachzählen musste, ob der Ausgang
 * wirklich fehlt. Zwei Karten für einen Gedanken sind eine Karte zu viel.
 *
 * ═══ DIE BELEGLAGE BLEIBT EHRLICH, AUCH NACH DER FREIGABE ═══
 *
 * Naheliegend wäre gewesen, nach dem Freigeben „vollständig" anzuzeigen. Dann
 * stünde da eine Unwahrheit: Der Ausgangslieferschein fehlt weiterhin. In einem
 * halben Jahr wüsste niemand mehr, ob wirklich beide Scheine da waren oder ob
 * jemand einmal durchgewinkt hat.
 */
function Belegkarte(eigenschaften: {
  auftrag: Auftragszeile;
  dokumente: Dokumentzeile[];
  gesperrt: boolean;
  offenerBeleg: string | null;
  auffrischen: () => void;
  melden: (fehler: Klarfehler) => void;
  freigeben: (grund: string) => void;
  zuruecknehmen: () => void;
  ansehen: (beleg: Dokumentzeile) => void;
  verwerfen: (beleg: Dokumentzeile) => void;
}) {
  const [grund, setGrund] = useState('');
  /** Die Datei, deren Löschen gerade bestätigt werden soll (HJ-032). */
  const [zuLoeschen, setZuLoeschen] = useState<Dokumentzeile | null>(null);
  const [formularOffen, setFormularOffen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [art, setArt] = useState('auftragsdokument');

  /**
   * ═══ WARUM DIE RÜCKMELDUNG HIER STEHT UND NICHT OBEN ═══
   *
   * Der Upload startet, sobald eine Datei gewählt ist — es gibt bewusst keinen
   * zweiten Knopf. Nur: Ging etwas schief, landete die Meldung über
   * `melden()` am SEITENANFANG, mehrere hundert Pixel über dieser Karte. Wer
   * unten eine Datei auswählt, sieht sie nie. Für ihn passiert schlicht nichts:
   * Der Dateiname steht neben dem Knopf, „Dokumente (0)" darüber, und keine
   * Erklärung dazwischen.
   *
   * Genau daraus wurde die Meldung „ein 71-KB-PDF wird blockiert" — die
   * Ablehnung war da, nur unsichtbar.
   *
   * Rückmeldung gehört an die Stelle, an der gehandelt wird. Deshalb hat diese
   * Karte jetzt ihren eigenen Fehler und ihren eigenen Fortschritt.
   */
  const [dateifehler, setDateifehler] = useState<string[] | null>(null);
  const [geschafft, setGeschafft] = useState<string | null>(null);

  const lage = beleglageText(eigenschaften.auftrag.beleglage);
  const vollstaendig = eigenschaften.auftrag.beleglage === 'vollstaendig';

  async function hochladen(dateien: FileList | null) {
    if (!dateien || dateien.length === 0) return;
    setLaeuft(true);
    setDateifehler(null);
    setGeschafft(null);

    // Jede Datei einzeln: Scheitert die zweite von dreien, sollen die anderen
    // beiden trotzdem liegen. Vorher brach die Schleife beim ersten Fehler ab.
    const gescheitert: string[] = [];
    let durch = 0;
    for (const datei of [...dateien]) {
      try {
        await dokumentHochladen(eigenschaften.auftrag.id, datei, art);
        durch += 1;
      } catch (problem) {
        // Was hier steht, muss die Frage „warum ging das nicht" allein
        // beantworten: welche Datei, welchen Typ der Browser gemeldet hat, wie
        // groß sie war und was der Server wörtlich sagte.
        const klar =
          problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem);
        const kb = Math.max(1, Math.round(datei.size / 1024));
        gescheitert.push(
          `${datei.name} (${datei.type || 'Typ unbekannt'}, ${kb} KB): ` +
            `${klar.original || klar.text}`,
        );
      }
    }

    setLaeuft(false);
    if (durch > 0) {
      setGeschafft(durch === 1 ? '1 Datei abgelegt.' : `${durch} Dateien abgelegt.`);
      eigenschaften.auffrischen();
    }
    if (gescheitert.length > 0) setDateifehler(gescheitert);
  }

  return (
    <Karte
      titel={`Dokumente (${eigenschaften.dokumente.length})`}
      werkzeuge={
        <div className="reihe" style={{ gap: 8 }}>
          {/* Bei unbekannter Beleglage liefert `beleglageText` einen
              Gedankenstrich. Als Marke gesetzt ergibt das eine leere Pille, die
              wie ein Anzeigefehler aussieht — dann lieber gar nichts. */}
          {lage.text !== '—' && <Marker art={lage.art}>{lage.text}</Marker>}
          {eigenschaften.auftrag.belege_freigegeben && (
            <Marker art="gut">freigegeben</Marker>
          )}
        </div>
      }
    >
      <div className="stapel">
        {eigenschaften.auftrag.belege_freigegeben && (
          <Meldung art="gut" titel="Diese Tour darf so abgerechnet werden">
            {eigenschaften.auftrag.belege_freigabe_grund}
            {eigenschaften.auftrag.belege_freigegeben_von_name && (
              <span className="meta">
                {' '}
                — freigegeben von {eigenschaften.auftrag.belege_freigegeben_von_name}
              </span>
            )}
          </Meldung>
        )}

        {eigenschaften.dokumente.length === 0 ? (
          <p className="stumm">
            Noch kein Dokument. Der Auftrag des Kunden gehört hierher, damit er
            nicht im Mailpostfach liegen bleibt.
          </p>
        ) : (
          <div className="belegreihe">
            {eigenschaften.dokumente.map((d) => (
              <div
                key={d.id}
                className={`belegkachel${
                  eigenschaften.offenerBeleg === d.id ? ' belegkachel--offen' : ''
                }`}
              >
                <button
                  className="belegkachel__flaeche"
                  onClick={() => eigenschaften.ansehen(d)}
                >
                  <span className="belegkachel__art">
                    {BELEGART[d.art as keyof typeof BELEGART] ?? d.art}
                  </span>
                  <span className="belegkachel__zeit">
                    {d.dateiname ?? zeitpunkt(d.erfasst_am)}
                    {d.hochgeladen_am === null && ' · noch unterwegs'}
                  </span>
                  <span className="belegkachel__oeffnen">
                    {eigenschaften.offenerBeleg === d.id ? 'Zuklappen' : 'Ansehen'}
                  </span>
                </button>

                {/* Ein Beleg ohne Datei — Testdaten, abgebrochener Upload —
                    lässt sich sonst nur ansehen und nicht loswerden. Er zählt
                    aber in die Beleglage hinein und lässt eine Tour vollständig
                    aussehen, die es nicht ist. */}
                {/* ═══ LÖSCHEN NUR NACH BESTÄTIGUNG (HJ-032) ═══
                    Hieß „Verwerfen" und löschte beim ersten Klick — Eintrag
                    UND Datei im Speicher, endgültig, einen Fingerbreit neben
                    „Ansehen". Jetzt heißt der Knopf, was er tut, und ein
                    Fenster fragt mit dem Dateinamen nach. */}
                {!eigenschaften.gesperrt && (
                  <button
                    className="belegkachel__weg"
                    aria-label={`${d.dateiname ?? 'Datei'} löschen`}
                    onClick={() => setZuLoeschen(d)}
                  >
                    Löschen
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {zuLoeschen && (
          <Bestaetigungsdialog
            titel="Datei endgültig löschen?"
            bestaetigungstext="Endgültig löschen"
            abbrechen={() => setZuLoeschen(null)}
            bestaetigen={() => {
              const datei = zuLoeschen;
              setZuLoeschen(null);
              eigenschaften.verwerfen(datei);
            }}
          >
            <p style={{ margin: '0 0 6px' }}>
              <strong>{zuLoeschen.dateiname ?? 'Ohne Dateinamen'}</strong>
              {' · '}
              {BELEGART[zuLoeschen.art as keyof typeof BELEGART] ?? zuLoeschen.art}
            </p>
            Die Datei wird aus dem Backoffice und aus dem Speicher entfernt — das
            lässt sich nicht rückgängig machen. Das Papieroriginal bleibt davon
            unberührt.
          </Bestaetigungsdialog>
        )}

        {!eigenschaften.gesperrt && (
          <div className="feldreihe">
            <Feld
              marke="Art der Datei"
              hinweis="Entscheidet, worauf die Beleglage sie anrechnet."
            >
              <select value={art} onChange={(e) => setArt(e.target.value)}>
                <option value="auftragsdokument">Transportauftrag des Kunden</option>
                <option value="eingangslieferschein">Eingangslieferschein</option>
                <option value="ausgangslieferschein">Ausgangslieferschein</option>
                <option value="nachweis">Nachweis</option>
                <option value="sonstiges">Sonstiges</option>
              </select>
            </Feld>

            <Feld
              marke="Datei"
              hinweis={
                laeuft
                  ? 'Wird abgelegt …'
                  : 'Sobald du eine Datei auswählst, wird sie abgelegt — es gibt keinen zweiten Knopf. Bilder und PDF, höchstens 20 MB je Datei.'
              }
            >
              <input
                type="file"
                multiple
                accept="image/*,application/pdf"
                disabled={laeuft}
                onChange={(e) => void hochladen(e.target.files)}
              />
            </Feld>
          </div>
        )}

        {/* ══ DIE RÜCKMELDUNG BLEIBT IN DIESER KARTE ══
            Siehe den Kopf von `hochladen()`: Oben auf der Seite sieht sie
            niemand, der hier unten eine Datei auswählt. */}
        {geschafft && <Meldung art="gut">{geschafft}</Meldung>}

        {dateifehler && (
          <Meldung art="fehler" titel="Diese Dateien kamen nicht durch">
            <ul className="fehltliste">
              {dateifehler.map((zeile) => (
                <li key={zeile}>{zeile}</li>
              ))}
            </ul>
          </Meldung>
        )}

        {/* --------------------------------------- Der Weg an der Lage vorbei */}
        {!vollstaendig &&
          !eigenschaften.auftrag.belege_freigegeben &&
          !eigenschaften.gesperrt &&
          (formularOffen ? (
            <Rueckfrage
              titel="Warum reichen die Belege hier?"
              bestaetigungstext="Belege freigeben"
              bestaetigungGesperrt={grund.trim().length < 3}
              abbrechen={() => {
                setFormularOffen(false);
                setGrund('');
              }}
              bestaetigen={() => {
                eigenschaften.freigeben(grund);
                setFormularOffen(false);
                setGrund('');
              }}
            >
              <Textfeld
                marke="Begründung"
                wert={grund}
                setzen={setGrund}
                autoFocus
                platzhalter="z. B. Kunde stellt keinen Ausgangsschein aus"
                hinweis={
                  'Pflichtangabe. Bei einer Prüfung lautet die Frage nie ' +
                  '„wurde freigegeben“, sondern „warum“.'
                }
              />
            </Rueckfrage>
          ) : (
            <div className="reihe" style={{ justifyContent: 'space-between' }}>
              <span className="meta">
                Manche Touren kommen mit einem einzigen Beleg aus — dafür gibt es
                keine Regel, die das System kennen könnte.
              </span>
              <Knopf art="zweit" klein onClick={() => setFormularOffen(true)}>
                Belege reichen trotzdem
              </Knopf>
            </div>
          ))}

        {eigenschaften.auftrag.belege_freigegeben && !eigenschaften.gesperrt && (
          <div className="reihe reihe--rechts">
            <Knopf art="still" klein onClick={eigenschaften.zuruecknehmen}>
              Belegfreigabe zurücknehmen
            </Knopf>
          </div>
        )}
      </div>
    </Karte>
  );
}

/* ------------------------------------------------------------------ Notiz */

/**
 * Die Büronotiz zur Tour.
 *
 * Dasselbe Muster wie das Eckdatenformular (H1.3b, E-039): Die Karte merkt
 * sich, welche Notiz beim Öffnen dastand. Frischt die Seite auf, folgt das
 * Feld still der neuen Notiz — es sei denn, man tippt gerade selbst. Dann
 * bleibt der eigene Text stehen, und hat inzwischen jemand anderes die Notiz
 * geändert, steht seine Fassung darunter, mit „Übernehmen" und „Meins
 * behalten". Vorher hielt die Karte ihren Text über jedes Auffrischen und
 * sogar über den Wechsel zu einer anderen Tour hinweg: „Notiz speichern"
 * hätte den Text von Tour A an Tour B geschrieben.
 */
function Notizkarte(eigenschaften: {
  /** Kennung der Tour — für die Kennung des Hinweises. */
  auftragId: string;
  notiz: string | null;
  gesperrt: boolean;
  laeuft: boolean;
  speichern: (text: string) => void;
}) {
  const frisch: Felder = { notiz: eigenschaften.notiz ?? '' };
  const [stand, setStand] = useState<Formularstand>(() => ({
    ausgang: frisch,
    eingaben: frisch,
    konflikte: [],
  }));
  const [zuletzt, setZuletzt] = useState(frisch.notiz);
  if (zuletzt !== frisch.notiz) {
    setZuletzt(frisch.notiz);
    setStand((bisher) =>
      abgleichen(bisher.ausgang, bisher.eingaben, frisch, {}, bisher.konflikte),
    );
  }

  const text = stand.eingaben.notiz ?? '';
  const geaendert = Object.keys(eigeneAenderungen(stand.ausgang, stand.eingaben)).length > 0;
  const konflikt = stand.konflikte[0] ?? null;
  const hinweisId = `konflikt-${eigenschaften.auftragId}-notiz`;

  return (
    <Karte titel="Notiz">
      <div className="stapel" style={{ maxWidth: 640 }}>
        <Feld
          marke="Was man zu dieser Tour wissen sollte"
          hinweis="Landet im Verlauf, sobald sie sich ändert."
        >
          <input
            type="text"
            value={text}
            disabled={eigenschaften.gesperrt}
            aria-describedby={konflikt ? hinweisId : undefined}
            onChange={(ereignis) =>
              setStand((bisher) => eingeben(bisher, 'notiz', ereignis.target.value))
            }
          />
          {konflikt && (
            <Konflikthinweis
              id={hinweisId}
              wert={konflikt.frischerWert?.trim() || 'leer'}
              uebernehmen={() => setStand((bisher) => fremdUebernehmen(bisher, 'notiz'))}
              behalten={() => setStand((bisher) => meinsBehalten(bisher, 'notiz'))}
            />
          )}
        </Feld>
        {/* Außerhalb des Labels, sonst würde der Satz Teil des Feldnamens. */}
        <p className="nurvorlesen" role="status">
          {konflikt ? 'Die Notiz wurde inzwischen von jemand anderem geändert.' : ''}
        </p>
        {geaendert && !eigenschaften.gesperrt && (
          <div className="reihe reihe--rechts">
            <Knopf
              art="still"
              onClick={() => setStand({ ausgang: frisch, eingaben: frisch, konflikte: [] })}
            >
              Verwerfen
            </Knopf>
            <Knopf
              art="haupt"
              klein
              disabled={konflikt !== null || eigenschaften.laeuft}
              onClick={() => eigenschaften.speichern(text)}
            >
              Notiz speichern
            </Knopf>
          </div>
        )}
      </div>
    </Karte>
  );
}

/* ---------------------------------------------------------------- Verlauf */

/**
 * Der Verlauf — mit einem ehrlichen Unterschied zwischen zwei Arten von Zeile.
 *
 * ═══ WARUM HIER ZWEIERLEI STEHT ═══
 *
 * Bei allen Touren, die vor dem Einbau des Protokolls entstanden sind, war
 * diese Karte leer, und das sah nach einem Fehler aus. Es war keiner:
 * Aufgezeichnet wird erst, seit es die Auslöser in der Datenbank gibt.
 *
 * Die Einträge nachträglich hineinzuschreiben wäre der falsche Ausweg gewesen —
 * ein Protokoll, in das man rückwirkend schreiben kann, taugt nicht mehr als
 * Nachweis. Stattdessen steht jetzt daneben, was die Daten selbst datiert
 * hergeben: wann die Tour eingetragen wurde, wann eine Fahrt entstand, wann ein
 * Beleg fotografiert wurde. Diese Zeilen sind erschlossen, nicht aufgezeichnet
 * — und das steht dran.
 */
function Verlaufskarte(eigenschaften: { zeilen: Verlaufszeile[] }) {
  const aufgezeichnet = eigenschaften.zeilen.filter(
    (z) => z.quelle === 'aufgezeichnet',
  ).length;

  return (
    <Karte titel="Verlauf" blank>
      <div style={{ padding: '12px 16px 0' }}>
        <p className="meta">
          {aufgezeichnet === 0
            ? 'Für diese Tour wurde noch nichts protokolliert — sie ist älter als das Protokoll. Was hier steht, ist aus den Daten selbst erschlossen. Jede Änderung ab jetzt wird aufgezeichnet.'
            : `${aufgezeichnet} ${aufgezeichnet === 1 ? 'Änderung ist' : 'Änderungen sind'} protokolliert. Die grau markierten Zeilen sind aus den Daten erschlossen, nicht aufgezeichnet.`}
        </p>
      </div>

      {eigenschaften.zeilen.length === 0 ? (
        <p className="stumm" style={{ padding: 16 }}>
          Zu dieser Tour gibt es noch keinen einzigen Anhaltspunkt — auch kein
          Anlagedatum. Das sollte nicht vorkommen.
        </p>
      ) : (
        <div className="tabellenrahmen" style={{ marginTop: 12 }}>
          <table className="tabelle">
            <thead>
              <tr>
                <th>Wann</th>
                <th>Was</th>
                <th>Änderung</th>
                <th>Wer</th>
                <th>Herkunft</th>
              </tr>
            </thead>
            <tbody>
              {eigenschaften.zeilen.map((z) => (
                <tr key={z.id}>
                  <td className="meta">{zeitpunkt(z.wann)}</td>
                  <td>{z.was}</td>
                  <td>{z.einzelheit ?? <span className="stumm">—</span>}</td>
                  <td className="meta">{z.wer ?? '—'}</td>
                  <td>
                    {z.quelle === 'aufgezeichnet' ? (
                      <Marker art="gut">aufgezeichnet</Marker>
                    ) : (
                      <Marker art="neutral">erschlossen</Marker>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Karte>
  );
}
